import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Cpu, Monitor, Keyboard, HardDrive, Headphones, Mouse, Package } from "lucide-react";

const Home = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [recommendedProducts, setRecommendedProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingRecommendations, setLoadingRecommendations] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // Icon mapping for categories
    const getCategoryIcon = (categoryName) => {
        const name = categoryName?.toLowerCase() || "";
        if (name.includes("cpu") || name.includes("processor") || name.includes("ซีพียู")) return Cpu;
        if (name.includes("monitor") || name.includes("จอ") || name.includes("screen")) return Monitor;
        if (name.includes("keyboard") || name.includes("คีย์บอร์ด")) return Keyboard;
        if (name.includes("storage") || name.includes("ssd") || name.includes("hdd") || name.includes("ฮาร์ดดิสก์")) return HardDrive;
        if (name.includes("headphone") || name.includes("audio") || name.includes("หูฟัง")) return Headphones;
        if (name.includes("mouse") || name.includes("เมาส์")) return Mouse;
        return Package; // default icon
    };

    // Color for categories - unified gray theme
    const getCategoryColor = () => {
        return { bg: "bg-gray-100", text: "text-gray-700" };
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        setIsLoggedIn(!!token);

        const fetchProducts = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/products/all`);
                setFeaturedProducts(response.data.slice(0, 8));
            } catch (error) {
                console.error("Error fetching products:", error);
            } finally {
                setLoading(false);
            }
        };

        const fetchRecommendations = async () => {
            try {
                if (token) {
                    // ดึงคำแนะนำสินค้าสำหรับผู้ใช้ที่ Login
                    const response = await axios.get(
                        `${API_BASE_URL}/api/recommendations/for-you?limit=8`,
                        { headers: { Authorization: token } }
                    );
                    setRecommendedProducts(response.data);
                } else {
                    // ดึงสินค้ายอดนิยมสำหรับ Guest
                    const response = await axios.get(
                        `${API_BASE_URL}/api/recommendations/popular?limit=8`
                    );
                    setRecommendedProducts(response.data);
                }
            } catch (error) {
                console.error("Error fetching recommendations:", error);
            } finally {
                setLoadingRecommendations(false);
            }
        };

        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/categories`);
                setCategories(response.data);
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setLoadingCategories(false);
            }
        };

        fetchProducts();
        fetchRecommendations();
        fetchCategories();
    }, []);

    const ProductCard = ({ product }) => (
        <Link
            to={`/product-details/${product.productId}`}
            className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
        >
            <div className="aspect-square bg-gray-100 overflow-hidden">
                <img
                    src={product.imageUrl ? `${API_BASE_URL}${product.imageUrl}` : "https://via.placeholder.com/300?text=No+Image"}
                    alt={product.productName}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=No+Image"; }}
                />
            </div>
            <div className="p-4">
                <h3 className="font-medium text-gray-900 truncate group-hover:text-red-600 transition-colors">
                    {product.productName}
                </h3>
                <p className="text-lg font-bold text-red-600 mt-1">
                    ฿{Number(product.price).toLocaleString()}
                </p>
            </div>
        </Link>
    );

    const ProductSkeleton = () => (
        <div className="bg-white rounded-xl p-4 animate-pulse">
            <div className="aspect-square bg-gray-200 rounded-lg mb-4" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Hero Section */}
            <section className="bg-black text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
                            อุปกรณ์<span className="text-red-500">ไอที</span>
                        </h1>
                        <p className="text-xl text-white/80 mb-10">
                            ค้นพบอุปกรณ์ไอทีคุณภาพดี ราคาสุดคุ้ม จัดส่งรวดเร็วทั่วประเทศ
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/all-products"
                                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-lg"
                            >
                                <ShoppingBag size={20} />
                                ช้อปเลย
                            </Link>
                            <Link
                                to="/about"
                                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gray-500 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                            >
                                เรียนรู้เพิ่มเติม
                                <ArrowRight size={20} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl font-bold text-gray-900">หมวดหมู่สินค้า</h2>
                        <p className="text-gray-600 mt-2">เลือกดูสินค้าตามประเภทที่คุณสนใจ</p>
                    </div>
                    {loadingCategories ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="animate-pulse">
                                    <div className="aspect-square bg-gray-200 rounded-xl" />
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mt-3" />
                                </div>
                            ))}
                        </div>
                    ) : categories.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {categories.map((category, index) => {
                                const IconComponent = getCategoryIcon(category.name);
                                const colors = getCategoryColor(index);
                                return (
                                    <Link
                                        key={category.categoryId}
                                        to={`/all-products?category=${category.categoryId}`}
                                        className="group text-center p-6 rounded-xl bg-gray-50 hover:shadow-lg hover:scale-105 transition-all duration-300"
                                    >
                                        <div className={`w-16 h-16 ${colors.bg} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                                            <IconComponent className={`w-8 h-8 ${colors.text}`} />
                                        </div>
                                        <h3 className="text-sm font-semibold text-gray-900 group-hover:text-red-600 transition-colors">
                                            {category.name}
                                        </h3>
                                    </Link>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <p>ไม่พบหมวดหมู่สินค้า</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Personalized Recommendations Section */}
            <section className="py-16 bg-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {isLoggedIn ? "แนะนำสำหรับคุณ" : "สินค้ายอดนิยม"}
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    {isLoggedIn ? "สินค้าที่เลือกมาเพื่อคุณโดยเฉพาะ" : "สินค้าที่ได้รับความนิยมสูงสุด"}
                                </p>
                            </div>
                            <Link to="/all-products" className="text-red-600 hover:text-red-700 font-medium flex items-center gap-1 text-sm">
                                ดูทั้งหมด <ArrowRight size={16} />
                            </Link>
                        </div>

                        {/* Products */}
                        <div className="p-6">
                            {loadingRecommendations ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    {[1, 2, 3, 4].map((i) => (
                                        <ProductSkeleton key={i} />
                                    ))}
                                </div>
                            ) : recommendedProducts.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    {recommendedProducts.map((product) => (
                                        <ProductCard key={product.productId} product={product} />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <p>ยังไม่มีสินค้าแนะนำในขณะนี้ ลองสำรวจสินค้าของเราดูสิ</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-16 bg-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">สินค้าทั้งหมด</h2>
                            <Link to="/all-products" className="text-red-600 hover:text-red-700 font-medium flex items-center gap-1 text-sm">
                                ดูทั้งหมด <ArrowRight size={16} />
                            </Link>
                        </div>

                        {/* Products */}
                        <div className="p-6">
                            {loading ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    {[1, 2, 3, 4].map((i) => (
                                        <ProductSkeleton key={i} />
                                    ))}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    {featuredProducts.map((product) => (
                                        <ProductCard key={product.productId} product={product} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
